// PROMPT 148: lo que se puede hacer con un mensaje del chat de WhatsApp de la plataforma:
// responderle citándolo, reaccionar, copiarlo, bajar el archivo, reenviarlo a otros chats,
// ver su información (horas de cada tilde, error explicado, lo que contó Meta) y reintentar
// el que falló. Todo lo que sale hacia el cliente pasa por el servidor, que decide si se puede
// (ventana de 24 h, permisos, chat de prueba): acá solo se muestran las acciones con sentido.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  ChevronDown,
  Copy,
  CornerUpLeft,
  Download,
  ExternalLink,
  Forward,
  Info,
  Loader2,
  RotateCcw,
  Search,
  Smile,
  SmilePlus,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { nombreCorto, nombreVisible } from "@/lib/marcasWa";
import {
  CATEGORIAS_EMOJI,
  REACCIONES_RAPIDAS,
  anotarFrecuente,
  leerFrecuentes,
} from "@/components/agente/emojis";

// WhatsApp no deja mandar un texto de más de 4096 caracteres.
export const MAX_TEXTO_WA = 4096;

// Lo que el chat le presta a cada burbuja (permisos, mensajes cargados, cita en curso).
export const ContextoChat = createContext(null);
export const useContextoChat = () => useContext(ContextoChat);

// El backend guarda las fechas en UTC y las devuelve sin la "Z" del final.
function fechaUtc(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  let t = String(ts);
  if (!/(Z|[+-]\d\d:?\d\d)$/i.test(t)) t += "Z";
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d;
}

function cuando(ts) {
  const d = fechaUtc(ts);
  if (!d) return null;
  return d.toLocaleString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

const recortar = (texto, max) => {
  const t = String(texto || "").replace(/\s+/g, " ").trim();
  return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

export function quienDe(m) {
  if (!m) return "";
  if (m.direccion === "entrante") return "Cliente";
  if (m.autor === "agente") return "Agente";
  if (m.desde_celular) return "Celular";
  return m.usuario || "Equipo";
}

const ETIQUETA_ARCHIVO = {
  imagen: "📷 Foto",
  audio: "🎵 Audio",
  video: "🎥 Video",
  documento: "📄 Documento",
  sticker: "Sticker",
};

// Una línea que cuenta de qué es el mensaje (para la cita y para la barra de abajo).
export function resumenDeMensaje(m) {
  if (!m) return "";
  if (m.eliminado) return "🚫 Mensaje eliminado";
  const media = m.media;
  if (media) {
    let base = ETIQUETA_ARCHIVO[media.tipo] || "Archivo";
    if (media.voz) base = "🎤 Nota de voz";
    else if (media.tipo === "documento" && media.nombre) base = `📄 ${media.nombre}`;
    return media.caption ? `${base} ${media.caption}` : base;
  }
  if (m.tipo === "ubicacion") return "📍 Ubicación";
  if (m.tipo === "contacto") return "👤 Contacto";
  if (m.tipo === "plantilla") return m.texto || `Plantilla ${(m.plantilla || {}).nombre || ""}`.trim();
  if (m.tipo === "reaccion") return `Reacción ${(m.reaccion || {}).emoji || ""}`.trim();
  return String(m.texto || "").trim() || "Mensaje";
}

// Apretar largo (0,5 s) en el celular abre el menú del mensaje.
export function useLongPress(alLargo, ms = 500) {
  const reloj = useRef(null);
  const limpiar = useCallback(() => {
    if (reloj.current) {
      clearTimeout(reloj.current);
      reloj.current = null;
    }
  }, []);
  useEffect(() => limpiar, [limpiar]);
  return useMemo(
    () => ({
      onTouchStart: () => {
        limpiar();
        reloj.current = setTimeout(() => {
          reloj.current = null;
          alLargo();
        }, ms);
      },
      onTouchEnd: limpiar,
      onTouchMove: limpiar,
      onTouchCancel: limpiar,
    }),
    [alLargo, limpiar, ms],
  );
}

// ---------------------------------------------------------------- emojis
export function SelectorEmojis({ onElegir }) {
  const [frecuentes, setFrecuentes] = useState(() => leerFrecuentes());
  const categorias = useMemo(
    () =>
      frecuentes.length
        ? [{ id: "frecuentes", titulo: "Frecuentes", icono: "🕒", emojis: frecuentes }, ...CATEGORIAS_EMOJI]
        : CATEGORIAS_EMOJI,
    [frecuentes],
  );
  const [idActiva, setIdActiva] = useState(frecuentes.length ? "frecuentes" : CATEGORIAS_EMOJI[0].id);
  const activa = categorias.find((c) => c.id === idActiva) || categorias[0];
  const elegir = (emoji) => {
    anotarFrecuente(emoji);
    setFrecuentes(leerFrecuentes());
    onElegir(emoji);
  };
  return (
    <div className="w-full" data-testid="wa-emojis">
      <div className="flex gap-1 border-b border-slate-100 pb-1">
        {categorias.map((c) => (
          <button
            key={c.id}
            type="button"
            title={c.titulo}
            aria-label={c.titulo}
            onClick={() => setIdActiva(c.id)}
            className={`flex h-8 w-8 items-center justify-center rounded-md text-lg ${
              c.id === activa.id ? "bg-slate-100" : "hover:bg-slate-50"
            }`}
            data-testid={`wa-emojis-cat-${c.id}`}
          >
            {c.icono}
          </button>
        ))}
      </div>
      <div className="mt-1 text-[11px] font-medium uppercase text-slate-400">{activa.titulo}</div>
      <div className="mt-1 grid max-h-44 grid-cols-8 gap-0.5 overflow-y-auto">
        {activa.emojis.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => elegir(emoji)}
            className="flex h-8 w-8 items-center justify-center rounded text-xl hover:bg-slate-100"
            data-testid={`wa-emoji-${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>
    </div>
  );
}

// El botón de la carita al lado del cuadro de escribir.
export function BotonEmojis({ onElegir, deshabilitado }) {
  const [abierto, setAbierto] = useState(false);
  return (
    <Popover open={abierto} onOpenChange={setAbierto}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={deshabilitado}
          title="Emojis"
          aria-label="Emojis"
          className="mb-0.5 shrink-0 rounded-full p-2 text-slate-500 hover:bg-black/5 hover:text-slate-700 disabled:opacity-40"
          data-testid="wa-emojis-abrir"
        >
          <Smile className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[min(18rem,calc(100vw-1.5rem))] p-2"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <SelectorEmojis onElegir={onElegir} />
      </PopoverContent>
    </Popover>
  );
}

// ---------------------------------------------------------------- cita
// "Respondiendo a ..." arriba del cuadro de escribir.
export function BarraRespuesta() {
  const ctx = useContextoChat();
  if (!ctx || !ctx.cita || !ctx.puedeEscribir) return null;
  return (
    <div
      className="flex items-center gap-2 border-l-4 border-emerald-500 bg-white px-3 py-2"
      data-testid="wa-respondiendo"
    >
      <CornerUpLeft className="h-4 w-4 shrink-0 text-emerald-600" />
      <div className="min-w-0 flex-1 truncate text-xs text-slate-500">
        <span className="font-medium text-emerald-700">Respondiendo a {ctx.cita.quien}:</span>{" "}
        {ctx.cita.texto}
      </div>
      <button
        type="button"
        onClick={ctx.usarCita}
        title="Quitar la cita"
        aria-label="Quitar la cita"
        className="shrink-0 rounded-full p-1 text-slate-400 hover:bg-black/5 hover:text-slate-700"
        data-testid="wa-respondiendo-quitar"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}

// El mensaje al que responde esta burbuja, adentro de la burbuja.
export function CitaEnBurbuja({ m, entrante }) {
  const ctx = useContextoChat();
  if (!m.responde_a) return null;
  const citado = (ctx && ctx.porWamid && ctx.porWamid.get(m.responde_a)) || null;
  const ir = () => {
    if (!citado) {
      toast.message("Es un mensaje anterior: cargá los mensajes anteriores para verlo.");
      return;
    }
    const el = document.querySelector(`[data-testid="wa-msg-${citado.id}"]`);
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    el.classList.add("bg-amber-100/70", "transition-colors");
    setTimeout(() => el.classList.remove("bg-amber-100/70"), 1500);
  };
  return (
    <button
      type="button"
      onClick={ir}
      className={`mb-1 block w-full min-w-[8rem] overflow-hidden rounded-md border-l-4 px-2 py-1 text-left text-xs ${
        entrante ? "border-emerald-500 bg-slate-100" : "border-emerald-600 bg-black/5"
      }`}
      data-testid={`wa-cita-${m.id}`}
    >
      <span className="block font-medium text-emerald-700">
        {citado ? quienDe(citado) : "Mensaje anterior"}
      </span>
      <span className="block truncate text-slate-600">
        {citado
          ? recortar(resumenDeMensaje(citado), 140)
          : "Es un mensaje anterior: cargá los mensajes anteriores para verlo."}
      </span>
    </button>
  );
}

// "Reenviado" y "Vino de un anuncio", arriba del contenido.
export function EtiquetasMensaje({ m }) {
  if (!m.reenviado && !m.anuncio) return null;
  const url = m.anuncio && /^https?:\/\//i.test(String(m.anuncio.url || "")) ? m.anuncio.url : null;
  return (
    <>
      {m.reenviado ? (
        <div
          className="mb-0.5 flex items-center gap-1 px-1 text-[11px] italic text-slate-500"
          data-testid={`wa-reenviado-${m.id}`}
        >
          <Forward className="h-3 w-3" />
          {m.reenviado === "muchas_veces" ? "Reenviado muchas veces" : "Reenviado"}
        </div>
      ) : null}
      {m.anuncio ? (
        <div
          className="mb-1 rounded-md bg-slate-100 px-2 py-1 text-xs text-slate-600"
          data-testid={`wa-anuncio-${m.id}`}
        >
          <span>Vino de un anuncio{m.anuncio.titulo ? `: ${m.anuncio.titulo}` : ""}</span>
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 inline-flex items-center gap-0.5 text-sky-700 hover:underline"
            >
              Ver anuncio <ExternalLink className="h-3 w-3" />
            </a>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

// Lo que WhatsApp avisa pero no deja ver (encuestas, GIF, pedidos...).
const NO_SOPORTADO = {
  poll: "una encuesta",
  poll_vote: "un voto en una encuesta",
  vote: "un voto en una encuesta",
  gif: "un GIF",
  view_once: "un mensaje para ver una sola vez",
  order: "un pedido",
  interactive: "un mensaje interactivo",
  button: "un mensaje con botones",
};

export function NoSoportado({ tipo, atiendeCelular }) {
  const que = NO_SOPORTADO[String(tipo || "").toLowerCase()] || "un mensaje de un tipo que no conocemos";
  return (
    <div className="px-1 pb-3 text-[13px] text-slate-600" data-testid="wa-no-soportado">
      <span className="italic">El cliente mandó {que}. WhatsApp no deja verlo acá.</span>
      {atiendeCelular ? <span> Miralo en el celular.</span> : null}
    </div>
  );
}

// ---------------------------------------------------------------- menú de cada mensaje
export function MenuMensaje({ m, abierto, setAbierto, reacciones, texto, tieneArchivo, onDescargar }) {
  const ctx = useContextoChat();
  const [verEmojis, setVerEmojis] = useState(false);
  if (!ctx || !ctx.real || m.direccion === "sistema" || m.tipo === "reaccion" || m.simulado) {
    return null;
  }
  const saliente = m.direccion !== "entrante";
  const eliminado = !!m.eliminado;
  const fallido = saliente && (m.estado_envio === "fallido" || m.estado_envio === "failed");
  const hayTexto = !!String(texto || "").trim();
  const puedeResponder = ctx.puedeEscribir && !!m.wa_message_id && !eliminado;
  const puedeReaccionar = puedeResponder && !fallido;
  const puedeCopiar = hayTexto && !eliminado;
  const puedeBajar = tieneArchivo && !eliminado;
  const puedeReenviar =
    ctx.modoPersona &&
    !eliminado &&
    (tieneArchivo || (hayTexto && !["plantilla", "ubicacion", "contacto"].includes(m.tipo)));
  const puedeVerInfo = saliente && !eliminado;
  const puedeReintentar = fallido && ctx.puedeReintentar;
  if (
    !puedeResponder &&
    !puedeCopiar &&
    !puedeBajar &&
    !puedeReenviar &&
    !puedeVerInfo &&
    !puedeReintentar
  ) {
    return null;
  }
  const miEmoji = ((reacciones || []).find((r) => r.direccion !== "entrante") || {}).emoji || "";

  const cambiar = (valor) => {
    setAbierto(valor);
    if (!valor) setVerEmojis(false);
  };
  const reaccionar = (emoji) => {
    setAbierto(false);
    setVerEmojis(false);
    ctx.onReaccionar(m, emoji === miEmoji ? "" : emoji);
  };
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(String(texto));
      toast.success("Copiado.");
    } catch {
      toast.error("No se pudo copiar. Seleccioná el texto y copialo a mano.");
    }
  };

  return (
    <DropdownMenu open={abierto} onOpenChange={cambiar} modal={false}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Acciones del mensaje"
          title="Acciones"
          className="absolute right-1 top-0.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-white/90 text-slate-500 opacity-0 shadow-sm transition-opacity hover:text-slate-800 focus-visible:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100 [@media(hover:none)]:pointer-events-none"
          data-testid={`wa-acciones-${m.id}`}
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align={saliente ? "end" : "start"}
        className="w-60 p-1"
        data-testid={`wa-menu-${m.id}`}
      >
        {puedeReaccionar ? (
          <div className="px-1 py-1" data-testid={`wa-accion-reaccionar-${m.id}`}>
            <div className="flex items-center justify-between gap-0.5">
              {REACCIONES_RAPIDAS.map((emoji) => (
                <button
                  key={emoji}
                  type="button"
                  onClick={() => reaccionar(emoji)}
                  title={emoji === miEmoji ? "Quitar la reacción" : `Reaccionar con ${emoji}`}
                  className={`flex h-8 w-8 items-center justify-center rounded-full text-lg hover:bg-slate-100 ${
                    emoji === miEmoji ? "bg-emerald-100" : ""
                  }`}
                  data-testid={`wa-reaccionar-${m.id}-${emoji}`}
                >
                  {emoji}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setVerEmojis((v) => !v)}
                title="Más emojis"
                aria-label="Más emojis"
                className="flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                data-testid={`wa-reaccionar-mas-${m.id}`}
              >
                <SmilePlus className="h-5 w-5" />
              </button>
            </div>
            {verEmojis ? (
              <div className="mt-1 border-t border-slate-100 pt-1">
                <SelectorEmojis onElegir={reaccionar} />
              </div>
            ) : null}
          </div>
        ) : null}
        {puedeReaccionar ? <DropdownMenuSeparator /> : null}
        {puedeResponder ? (
          <DropdownMenuItem
            onSelect={() => ctx.onResponder(m)}
            data-testid={`wa-accion-responder-${m.id}`}
          >
            <CornerUpLeft className="mr-2 h-4 w-4" /> Responder
          </DropdownMenuItem>
        ) : null}
        {puedeCopiar ? (
          <DropdownMenuItem onSelect={copiar} data-testid={`wa-accion-copiar-${m.id}`}>
            <Copy className="mr-2 h-4 w-4" /> Copiar
          </DropdownMenuItem>
        ) : null}
        {puedeBajar ? (
          <DropdownMenuItem onSelect={onDescargar} data-testid={`wa-accion-descargar-${m.id}`}>
            <Download className="mr-2 h-4 w-4" /> Descargar
          </DropdownMenuItem>
        ) : null}
        {puedeReenviar ? (
          <DropdownMenuItem
            onSelect={() => ctx.onReenviar(m)}
            data-testid={`wa-accion-reenviar-${m.id}`}
          >
            <Forward className="mr-2 h-4 w-4" /> Reenviar
          </DropdownMenuItem>
        ) : null}
        {puedeVerInfo ? (
          <DropdownMenuItem onSelect={() => ctx.onInfo(m)} data-testid={`wa-accion-info-${m.id}`}>
            <Info className="mr-2 h-4 w-4" /> Información
          </DropdownMenuItem>
        ) : null}
        {puedeReintentar ? (
          <DropdownMenuItem
            onSelect={() => ctx.onReintentar(m)}
            data-testid={`wa-accion-reintentar-${m.id}`}
          >
            <RotateCcw className="mr-2 h-4 w-4" /> Reintentar
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ---------------------------------------------------------------- información
const PASOS_INFO = [
  { clave: "sent", etiqueta: "Enviado", rango: 1 },
  { clave: "delivered", etiqueta: "Entregado", rango: 2 },
  { clave: "read", etiqueta: "Leído", rango: 3 },
  { clave: "played", etiqueta: "Reproducido", rango: 4 },
];
const RANGO_ESTADO = { enviado: 1, sent: 1, delivered: 2, read: 3, played: 4 };
const CATEGORIA_META = {
  service: "Servicio",
  utility: "Utilidad",
  marketing: "Marketing",
  marketing_lite: "Marketing",
  authentication: "Autenticación",
  authentication_international: "Autenticación",
  referral_conversion: "Respuesta a un anuncio",
};
const TIPO_META = {
  regular: "con cobro",
  free_customer_service: "sin cargo (respuesta al cliente)",
  free_entry_point: "sin cargo (viene de un anuncio)",
};

export function InfoMensaje({ m, onCerrar }) {
  const rango = RANGO_ESTADO[m.estado_envio] || 0;
  const horas = m.estados_envio || {};
  const pasos = PASOS_INFO.filter((p) => horas[p.clave] || rango >= p.rango);
  const hayError = !!(
    m.error ||
    m.error_explicado ||
    m.estado_envio === "fallido" ||
    m.estado_envio === "failed"
  );
  const precio = m.pricing || null;
  const categoria = precio ? CATEGORIA_META[precio.categoria] || precio.categoria : null;
  const tipoPrecio = precio ? TIPO_META[precio.tipo] || precio.tipo : null;
  return (
    <Dialog
      open
      onOpenChange={(abre) => {
        if (!abre) onCerrar();
      }}
    >
      <DialogContent className="max-w-md" data-testid={`wa-info-${m.id}`}>
        <DialogHeader>
          <DialogTitle>Información del mensaje</DialogTitle>
          <DialogDescription>{recortar(resumenDeMensaje(m), 120)}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="text-slate-600">
            Lo mandó: <span className="font-medium text-slate-800">{quienDe(m)}</span>
            {cuando(m.created_at) ? ` · ${cuando(m.created_at)}` : ""}
          </div>
          {pasos.length ? (
            <ul className="space-y-1" data-testid="wa-info-pasos">
              {pasos.map((p) => (
                <li
                  key={p.clave}
                  className="flex items-center justify-between rounded-md bg-slate-50 px-2 py-1"
                >
                  <span className="text-slate-700">{p.etiqueta}</span>
                  <span className="text-slate-500">{cuando(horas[p.clave]) || "sin hora"}</span>
                </li>
              ))}
            </ul>
          ) : !hayError ? (
            <p className="text-slate-500">Todavía no hay novedades de WhatsApp sobre este mensaje.</p>
          ) : null}
          {hayError ? (
            <div
              className="rounded-md border border-red-200 bg-red-50 p-2 text-red-800"
              data-testid="wa-info-error"
            >
              <div className="font-medium">No se pudo mandar</div>
              <div>{m.error_explicado || m.error || "WhatsApp no lo aceptó."}</div>
              {m.error_detalle ? <div className="mt-1 text-xs">{m.error_detalle}</div> : null}
              {m.error_codigo || (m.error && m.error_explicado) ? (
                <div className="mt-1 text-[11px] text-red-700/80">
                  Meta: {m.error_codigo ? `código ${m.error_codigo}` : ""}
                  {m.error && m.error_explicado ? ` · ${String(m.error).slice(0, 200)}` : ""}
                </div>
              ) : null}
            </div>
          ) : null}
          {categoria ? (
            <div
              className="rounded-md bg-slate-50 px-2 py-1.5 text-slate-700"
              data-testid="wa-info-precio"
            >
              <div>
                Meta lo contó como: <span className="font-medium">{categoria}</span>
                {tipoPrecio ? ` · ${tipoPrecio}` : ""}
              </div>
              <div className="mt-0.5 text-[11px] text-slate-500">
                Es lo que avisó Meta. Lo que se paga lo factura Meta (o YCloud).
              </div>
            </div>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- reenviar
const MAX_REENVIO = 10;

export function ReenviarDialog({ m, conversaciones, actualId, marcas, onHecho, onCerrar }) {
  const [q, setQ] = useState("");
  const [elegidas, setElegidas] = useState([]);
  const [enviando, setEnviando] = useState(false);
  const [resultados, setResultados] = useState(null);
  const candidatas = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (conversaciones || [])
      .filter((c) => c.canal === "whatsapp" && c.id !== actualId)
      .filter((c) => !t || `${nombreVisible(c)} ${c.telefono || ""}`.toLowerCase().includes(t));
  }, [conversaciones, actualId, q]);
  const motivoDe = (c) => {
    if (c.estado === "cerrada") return "Está archivado: reabrilo antes de escribir.";
    if (c.ventana_abierta === false) return "Pasaron 24 horas desde el último mensaje del cliente.";
    return null;
  };
  const alternar = (c) => {
    if (motivoDe(c)) return;
    if (elegidas.includes(c.id)) {
      setElegidas((lista) => lista.filter((id) => id !== c.id));
    } else if (elegidas.length >= MAX_REENVIO) {
      toast.message(`Podés reenviar a ${MAX_REENVIO} chats como máximo.`);
    } else {
      setElegidas((lista) => [...lista, c.id]);
    }
  };
  const enviar = async () => {
    if (!elegidas.length || enviando) return;
    setEnviando(true);
    try {
      const { data } = await api.post(`/whatsapp/mensajes/${m.id}/reenviar`, {
        conversaciones: elegidas,
      });
      const res = data?.resultados || [];
      onHecho?.(data);
      if (res.length && res.every((r) => r.ok)) {
        toast.success(res.length === 1 ? "Reenviado a 1 chat." : `Reenviado a ${res.length} chats.`);
        onCerrar();
      } else {
        setResultados(res);
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setEnviando(false);
    }
  };
  return (
    <Dialog
      open
      onOpenChange={(abre) => {
        if (!abre) onCerrar();
      }}
    >
      <DialogContent className="max-w-md" data-testid="wa-reenviar">
        <DialogHeader>
          <DialogTitle>Reenviar mensaje</DialogTitle>
          <DialogDescription>
            {recortar(resumenDeMensaje(m), 100)} · Elegí hasta {MAX_REENVIO} chats.
          </DialogDescription>
        </DialogHeader>
        {resultados ? (
          <div className="space-y-3">
            <ul className="space-y-1 text-sm" data-testid="wa-reenviar-resultados">
              {resultados.map((r) => (
                <li key={r.conversacion_id} className={r.ok ? "text-emerald-700" : "text-red-700"}>
                  {r.ok ? "✓" : "✗"} {r.nombre || "Chat"}
                  {r.ok ? "" : `: ${r.motivo || "no se pudo mandar."}`}
                </li>
              ))}
            </ul>
            <div className="flex justify-end">
              <Button onClick={onCerrar}>Cerrar</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar por nombre o teléfono"
                className="pl-8"
                data-testid="wa-reenviar-buscar"
              />
            </div>
            <ul className="max-h-72 space-y-0.5 overflow-y-auto">
              {candidatas.length === 0 ? (
                <li className="px-2 py-3 text-sm text-slate-500">
                  No hay chats de WhatsApp para elegir en la lista.
                </li>
              ) : null}
              {candidatas.map((c) => {
                const motivo = motivoDe(c);
                const marcada = elegidas.includes(c.id);
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      disabled={!!motivo}
                      onClick={() => alternar(c)}
                      className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${
                        motivo ? "cursor-not-allowed opacity-60" : "hover:bg-slate-50"
                      } ${marcada ? "bg-emerald-50" : ""}`}
                      data-testid={`wa-reenviar-chat-${c.id}`}
                    >
                      <span
                        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border text-[10px] ${
                          marcada
                            ? "border-emerald-600 bg-emerald-600 text-white"
                            : "border-slate-300"
                        }`}
                      >
                        {marcada ? "✓" : ""}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate font-medium text-slate-800">
                          {nombreVisible(c)}
                        </span>
                        <span className="block truncate text-xs text-slate-500">
                          {[nombreCorto(c.marca_slug, marcas), motivo].filter(Boolean).join(" · ")}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={onCerrar}>
                Cancelar
              </Button>
              <Button
                onClick={enviar}
                disabled={!elegidas.length || enviando}
                data-testid="wa-reenviar-enviar"
              >
                {enviando ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Forward className="mr-2 h-4 w-4" />
                )}
                Reenviar{elegidas.length ? ` (${elegidas.length})` : ""}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- todo junto
// Arma lo que comparten las burbujas y los cuadros de información y de reenviar.
// `acciones` lo da la página: { cita, setCita, conversaciones, marcas }.
export function useAccionesChat({
  conversacion,
  acciones,
  modoPersona,
  ventanaCerrada,
  cerrada,
  onHecho,
}) {
  const convId = (conversacion && conversacion.id) || null;
  const real = !!conversacion && conversacion.canal === "whatsapp";
  const atiendeCelular = !!(conversacion && conversacion.atiende_celular);
  const cita = (acciones && acciones.cita) || null;
  const setCita = acciones && acciones.setCita;
  const conversaciones = (acciones && acciones.conversaciones) || [];
  const marcas = (acciones && acciones.marcas) || [];
  const mensajes = conversacion && conversacion.mensajes;
  const [infoId, setInfoId] = useState(null);
  const [reenviarId, setReenviarId] = useState(null);
  const [ocupado, setOcupado] = useState(null);

  const porWamid = useMemo(() => {
    const mapa = new Map();
    (mensajes || []).forEach((x) => {
      if (x.wa_message_id) mapa.set(x.wa_message_id, x);
    });
    return mapa;
  }, [mensajes]);

  const onResponder = useCallback(
    (m) => {
      if (!setCita) return;
      setCita({
        id: m.id,
        quien: quienDe(m),
        texto: recortar(resumenDeMensaje(m), 120),
      });
      setTimeout(() => {
        const area = document.querySelector('[data-testid="wa-texto"]');
        if (area) area.focus();
      }, 80);
    },
    [setCita],
  );
  const usarCita = useCallback(() => {
    if (setCita) setCita(null);
  }, [setCita]);

  const onReaccionar = useCallback(
    async (m, emoji) => {
      if (!convId || ocupado) return;
      setOcupado(m.id);
      try {
        const { data } = await api.post(`/whatsapp/conversaciones/${convId}/reaccion`, {
          mensaje_id: m.id,
          emoji,
        });
        if (onHecho) onHecho({ conversacion: { id: convId }, envio: data && data.envio });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setOcupado(null);
      }
    },
    [convId, ocupado, onHecho],
  );

  const onReintentar = useCallback(
    async (m) => {
      if (!convId || ocupado) return;
      setOcupado(m.id);
      try {
        const { data } = await api.post(`/whatsapp/mensajes/${m.id}/reintentar`, {});
        if (data && data.envio && data.envio.ok) toast.success("Se volvió a mandar.");
        if (onHecho) onHecho({ conversacion: { id: convId }, envio: data && data.envio });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail));
      } finally {
        setOcupado(null);
      }
    },
    [convId, ocupado, onHecho],
  );

  const contexto = useMemo(
    () => ({
      real,
      modoPersona: !!modoPersona,
      puedeEscribir: real && !!modoPersona && !cerrada && !ventanaCerrada,
      puedeReintentar: real && !!modoPersona && !cerrada,
      atiendeCelular,
      porWamid,
      cita,
      ocupado,
      onResponder,
      usarCita,
      onReaccionar,
      onReintentar,
      onInfo: (m) => setInfoId(m.id),
      onReenviar: (m) => setReenviarId(m.id),
    }),
    [
      real,
      modoPersona,
      cerrada,
      ventanaCerrada,
      atiendeCelular,
      porWamid,
      cita,
      ocupado,
      onResponder,
      usarCita,
      onReaccionar,
      onReintentar,
    ],
  );

  const infoDe = infoId ? (mensajes || []).find((x) => x.id === infoId) : null;
  const reenviarDe = reenviarId ? (mensajes || []).find((x) => x.id === reenviarId) : null;
  const dialogos = (
    <>
      {infoDe ? <InfoMensaje m={infoDe} onCerrar={() => setInfoId(null)} /> : null}
      {reenviarDe ? (
        <ReenviarDialog
          m={reenviarDe}
          conversaciones={conversaciones}
          actualId={convId}
          marcas={marcas}
          onHecho={() => onHecho && onHecho({ conversacion: { id: convId } })}
          onCerrar={() => setReenviarId(null)}
        />
      ) : null}
    </>
  );
  return { contexto, dialogos };
}
